import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import BillingAddress from '../../../components/V2/BillingAddress';
import WeekSlider from './WeekSlider';
import OrderList from './OrderList';
import DeliveryDay from './DeliveryDay';
import { useDeliveryDays, useCutoffTime } from './hooks';
import Loading from 'core/ui/components/Loading';
import { isLoading } from 'core/api/loadingHelpers';
import { Box, Container, Flex, H3, Hr, Page, Text } from 'core/ui/atoms';

function UserManagementPage() {
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);

  // Fetch delivery days
  const {
    loading: deliveryDaysLoading,
    error: deliveryDaysError,
    deliveryDays,
    firstPeriod,
  } = useDeliveryDays();

  // Current selected delivery period
  const currentDeliveryPeriod = deliveryDays[selectedWeekIndex] || firstPeriod;

  // Fetch cutoff time for selected period
  const {
    loading: cutoffTimeLoading,
    error: cutoffTimeError,
    cutoffDateTime,
    formattedDateTime,
  } = useCutoffTime(currentDeliveryPeriod?.id);

  // Handle week selection change
  const handleWeekChange = (deliveryDayId) => {
    const weekIndex = deliveryDays.findIndex((day) => day.id === deliveryDayId);
    if (weekIndex !== -1) {
      setSelectedWeekIndex(weekIndex);
    }
  };

  // Loading states
  const isLoadingDeliveryDays = isLoading(deliveryDaysLoading, deliveryDays, deliveryDaysError);
  const isLoadingCutoffTime = isLoading(cutoffTimeLoading, formattedDateTime.time, cutoffTimeError);

  if (isLoadingDeliveryDays) {
    return <Loading />;
  }

  if (!firstPeriod) {
    return null;
  }

  if (isLoadingCutoffTime) {
    return <Loading />;
  }

  const ordersDescriptionText = () => {
    const now = moment();
    const cutoffDate = moment(cutoffDateTime);
    const TenDaysLater = cutoffDate.add(2, 'days');
    if (cutoffDate.isBefore(now)) {
      return `The cut-off to edit this order has passed. If you need to make changes to your order, email us at help@gomethodology.com by 9:00 AM PT on ${TenDaysLater.format('dddd, MMMM Do')}.`;
    }
    if (cutoffDate.isAfter(now)) {
      return `Edit this week's order until ${formattedDateTime.time} PST on ${formattedDateTime.date}`;
    }
  };

  return (
    <Page scrollTop>
      <MainContainer>
        <DashboardSection>
          <OrdersHeading>Weekly Menu</OrdersHeading>
          <OrdersDescription>{ordersDescriptionText()}</OrdersDescription>
          <WeekSlider
            defaultWeek={firstPeriod}
            deliveryPeriods={deliveryDays}
            selectedWeek={selectedWeekIndex}
            onChangeWeek={handleWeekChange}
          />
          <ContentContainer direction='column' alignItems='center'>
            <OrderList
              deliveryPeriod={currentDeliveryPeriod}
            />
            <Hr />
            <AddressSection>
              <AddressTitle>This Week's Delivery Address</AddressTitle>
              <BillingAddress />
            </AddressSection>
            <DeliveryDay />
          </ContentContainer>
        </DashboardSection>
      </MainContainer>
    </Page>
  );
}

// Styled Components
const MainContainer = styled.div`
  ${(p) => p.theme.max('md')`
    border-radius: 40px 40px 0 0;
  `}
`;

const DashboardSection = styled(Container)`
  position: relative;
  padding: 30px 40px 0 40px;

  ${(p) => p.theme.max('sm')`
    padding: 20px 0 0 0;
  `}
`;

const SectionHeading = styled(H3)`
  font-size: 1.25em;
  font-weight: 500;
  margin-top: 0;
  text-transform: capitalize;

  ${(p) => p.theme.max('sm')`
    font-size: 0.875em;
  `}
`;

const OrdersHeading = styled(SectionHeading)`
  margin: 0;
  position: absolute;
  top: 30px;
  left: 40px;
  color: ${(p) => p.theme.typography.colors.primary};

  ${(p) => p.theme.max('sm')`
    position: static;
    font-size: 30px;
    text-align: center;
    font-family: ${({ theme }) => theme.typography.familyLarken};
  `}
`;

const OrdersDescription = styled.p`
  padding: 0 70px;
  margin: 0 0 27px 0;

  ${(p) => p.theme.max('sm')`
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    font-family: ${({ theme }) => theme.typography.familyPoppins};
  `}
`;

const ContentContainer = styled(Flex)``;

const AddressSection = styled(Box)`
  width: 90%;
`;

const AddressTitle = styled(Text)`
  text-align: center;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyLarken};
`;


export default UserManagementPage;
